import { createContext, PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useArchiveContext } from './ArchiveContext'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useToastContext } from './ToastContext'
import { useAction } from '../../hooks/action'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { useProvideContext } from '../../hooks/context'
import { mapReadTableResponse, mapReadResponse, throwIfResponseNotOk } from '../../tools/api'
import CalibrationRod from '../../models/CalibrationRod'
import TableControl from '../../models/TableControl'

export interface CalibrationRodContextValue extends DataContextValue {
    calibrationRods: TableControl<CalibrationRod>
    selectedCalibrationRod?: CalibrationRod
    archiveCalibrationRod: (calibrationRodId?: string) => Promise<void>
    restoreCalibrationRod: (calibrationRodId?: string) => Promise<void>
}

const CalibrationRodContext = createContext<CalibrationRodContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const params = useParams()
    const { includeArchived } = useArchiveContext()
    const { calibrationRodApi } = useApiContext()
    const { selectedOrganization } = useUserContext()
    const Toast = useToastContext()
    const calibrationRodId = params.calibrationRodId

    const [calibrationRods, calibrationRodsLoading, calibrationRodsError, refreshCalibrationRods] =
        usePaginatedTable(
            async (table: TableControl<CalibrationRod>) => {
                const orgId = selectedOrganization?.id
                if (!orgId) return null
                const response = await calibrationRodApi.getAllCalibrationRodsV2({
                    tenantId: orgId,
                    sortBy: table.sortBy,
                    ascending: table.ascending,
                    skip: table.skip,
                    take: table.take,
                    includeArchived,
                })
                return mapReadTableResponse(response, table, CalibrationRod)
            },
            [selectedOrganization?.id, includeArchived]
        )

    const [selectedCalibrationRod, selectedLoading, selectedError, refreshSelectedCalibrationRod] =
        useImmutableObject(async () => {
            const orgId = selectedOrganization?.id
            if (!(orgId && calibrationRodId)) return
            const response = await calibrationRodApi.getCalibrationRodV2({
                rodId: calibrationRodId,
                tenantId: orgId,
            })
            return mapReadResponse(response, CalibrationRod)
        }, [calibrationRodId, selectedOrganization?.id])

    const [archiveLoading, archiveError, archiveCalibrationRod] = useAction(
        async (calibrationRodId?: string) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && calibrationRodId)) return
            const response = await calibrationRodApi.archiveCalibrationRodV2({
                rodId: calibrationRodId,
                tenantId: orgId,
            })
            throwIfResponseNotOk(response)
            refreshCalibrationRods()
            Toast.completed('archive', 'calibration rod')
        }
    )

    const [restoreLoading, restoreError, restoreCalibrationRod] = useAction(
        async (calibrationRodId?: string) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && calibrationRodId)) return
            const response = await calibrationRodApi.restoreCalibrationRodV2({
                rodId: calibrationRodId,
                tenantId: orgId,
            })
            throwIfResponseNotOk(response)
            if (calibrationRodId === selectedCalibrationRod?.id) refreshSelectedCalibrationRod()
            refreshCalibrationRods()
            Toast.completed('restore', 'calibration rod')
        }
    )

    const loading = selectedLoading || archiveLoading || restoreLoading
    const error = selectedError || archiveError || restoreError

    useEffect(() => {
        if (error) Toast.error(error, 'CalibrationRod error')
    }, [error])

    const value: CalibrationRodContextValue = {
        loading,
        error,

        calibrationRods,
        selectedCalibrationRod,
        archiveCalibrationRod,
        restoreCalibrationRod,
    }

    return <CalibrationRodContext.Provider value={value}>{children}</CalibrationRodContext.Provider>
}

export const useCalibrationRodContext = () =>
    useProvideContext(CalibrationRodContext, 'CalibrationRod')
